import { joinRoom, selfId, type JsonValue, type MessageAction, type Room } from 'trystero';
import { matchBosses, matchDeck, type SideLoadout } from '../meta/profile';
import type { BoardMode, Side } from '../data/types';
import type { FoundMatch, NetCommand, NetLink } from './types';

const APP = 'peasents-demo';
const ROOM = 'battle-queue';

type Payload = { [key: string]: JsonValue };

interface HelloMsg {
  commander: string;
  levels: Record<string, number>;
  crit: number;
  deck: string[];
  bosses: string[];
}

/**
 * Pairs two open games with no account and no API key. Phones, iPads, and
 * computers find each other through public relays, then talk directly.
 */
export function startAutoSearch(opts: {
  commander: string;
  loadout: SideLoadout;
  deck: string[];
  bosses: string[];
  board?: BoardMode;
  onFound: (match: FoundMatch) => void;
  onStatus: (detail: string) => void;
}): () => void {
  if (!window.isSecureContext || !crypto.subtle) {
    opts.onStatus('This page is not secure, so the phone cannot join. Use the https link.');
    return () => {};
  }
  let room: ReturnType<typeof joinRoom>;
  try {
    room = joinRoom({ appId: APP }, ROOM);
  } catch {
    opts.onStatus('Could not open the match network. Still searching.');
    return () => {};
  }
  const wire = room.makeAction<Payload>('mm');
  const peers = new Set<string>();
  const busy = new Set<string>();
  let handed = false;
  let stopped = false;
  let partner: string | null = null;
  let theirHello: HelloMsg | null = null;
  let startSent = false;

  const mine = (): Payload => ({
    t: 'hello',
    commander: opts.commander,
    levels: { ...opts.loadout.levels } as { [key: string]: JsonValue },
    crit: opts.loadout.crit,
    deck: opts.deck,
    bosses: opts.bosses,
  });

  const stop = (): void => {
    if (stopped || handed) return;
    stopped = true;
    void room.leave();
  };

  const go = (side: Side, seed: number, startAt: number, hello: HelloMsg, board: BoardMode): void => {
    if (handed || stopped || !partner) return;
    handed = true;
    const opponent: SideLoadout = { levels: { ...hello.levels }, crit: hello.crit };
    const self: SideLoadout = { levels: { ...opts.loadout.levels }, crit: opts.loadout.crit };
    const mineDeck = matchDeck(opts.deck);
    const mineBosses = matchBosses(opts.bosses);
    const theirDeck = matchDeck(hello.deck);
    const theirBosses = matchBosses(hello.bosses);
    const mate = partner;
    opts.onFound({
      matchId: `auto:${[selfId, mate].sort().join(':')}`,
      seed,
      board,
      loadout: side === 0 ? [self, opponent] : [opponent, self],
      decks: side === 0 ? [mineDeck, theirDeck] : [theirDeck, mineDeck],
      bosses: side === 0 ? [mineBosses, theirBosses] : [theirBosses, mineBosses],
      link: autoLink(room, wire, mate, side, startAt, hello.commander),
    });
  };

  const tellBusy = (): void => {
    for (const id of peers) {
      if (id !== partner) void wire.send({ t: 'busy' }, { target: id });
    }
  };

  const offer = (): void => {
    if (handed || stopped || partner) return;
    const waiting = [selfId, ...[...peers].filter((id) => !busy.has(id))].sort();
    const index = waiting.indexOf(selfId);
    if (index < 0 || index % 2 === 1) return;
    const mate = waiting[index + 1];
    if (!mate) return;
    partner = mate;
    void wire.send(mine(), { target: mate });
    opts.onStatus('Another player is here. Connecting.');
  };

  room.onPeerJoin = (peerId) => {
    peers.add(peerId);
    offer();
  };

  room.onPeerLeave = (peerId) => {
    peers.delete(peerId);
    busy.delete(peerId);
    if (!handed && partner === peerId) {
      partner = null;
      theirHello = null;
      startSent = false;
      offer();
    }
  };

  wire.onMessage = (msg, { peerId }) => {
    if (stopped || handed) return;
    if (msg.t === 'busy') {
      busy.add(peerId);
      if (partner === peerId) {
        partner = null;
        theirHello = null;
        startSent = false;
        offer();
      }
      return;
    }
    if (msg.t === 'hello') {
      const hello = helloOf(msg);
      if (!hello) return;
      if (selfId < peerId) {
        partner = peerId;
        theirHello = hello;
        if (!startSent) {
          startSent = true;
          const seed = (Date.now() % 2147483646) + 1;
          const startAt = Date.now() + 2000;
          const board: BoardMode = opts.board === 'painted' ? 'painted' : 'arrows';
          void wire.send({ t: 'start', seed, startAt, board }, { target: peerId });
          tellBusy();
          go(0, seed, startAt, hello, board);
        }
        return;
      }
      if (!partner) {
        partner = peerId;
        theirHello = hello;
        void wire.send(mine(), { target: peerId });
      }
      return;
    }
    if (msg.t === 'start' && typeof msg.seed === 'number' && typeof msg.startAt === 'number' && partner === peerId && theirHello) {
      tellBusy();
      go(1, msg.seed, msg.startAt, theirHello, msg.board === 'painted' ? 'painted' : 'arrows');
    }
  };

  opts.onStatus('Looking for another phone, iPad, or computer');
  return stop;
}

function helloOf(msg: Payload): HelloMsg | null {
  if (msg.t !== 'hello' || typeof msg.commander !== 'string' || typeof msg.crit !== 'number') return null;
  if (!msg.levels || typeof msg.levels !== 'object' || Array.isArray(msg.levels)) return null;
  const levels: Record<string, number> = {};
  for (const [key, value] of Object.entries(msg.levels)) {
    if (typeof value === 'number') levels[key] = value;
  }
  const ids = (value: JsonValue | undefined): string[] =>
    Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
  return { commander: msg.commander, levels, crit: msg.crit, deck: ids(msg.deck), bosses: ids(msg.bosses) };
}

function commandOf(value: JsonValue | undefined): NetCommand | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { op, tick, side, n, tile, from, to, branch } = value;
  if (
    (op !== 'summon' && op !== 'lane' && op !== 'drop' && op !== 'buy' && op !== 'commander') ||
    typeof tick !== 'number' ||
    (side !== 0 && side !== 1) ||
    typeof n !== 'number'
  ) {
    return null;
  }
  const cmd: NetCommand = { tick, side, n, op };
  if (typeof tile === 'number') cmd.tile = tile;
  if (typeof from === 'number') cmd.from = from;
  if (typeof to === 'number') cmd.to = to;
  if (typeof branch === 'string') cmd.branch = branch;
  return cmd;
}

function autoLink(
  room: Room,
  wire: MessageAction<Payload>,
  partner: string,
  localSide: Side,
  startAt: number,
  opponentCommander: string,
): NetLink {
  const inbox: NetCommand[] = [];
  let last = Date.now();
  const previous = wire.onMessage;
  wire.onMessage = (msg, ctx) => {
    void previous?.(msg, ctx);
    if (ctx.peerId !== partner) return;
    if (msg.t === 'ping') last = Date.now();
    if (msg.t === 'cmd') {
      const cmd = commandOf(msg.cmd);
      if (cmd) inbox.push(cmd);
      last = Date.now();
    }
  };
  const ping = window.setInterval(() => {
    void wire.send({ t: 'ping' }, { target: partner });
  }, 1000);
  return {
    localSide,
    startAt,
    opponentCommander,
    send: (cmd) => {
      void wire.send({ t: 'cmd', cmd: JSON.parse(JSON.stringify(cmd)) as JsonValue }, { target: partner });
    },
    take: () => inbox.splice(0, inbox.length),
    silenceMs: () => Date.now() - last,
    close: () => {
      window.clearInterval(ping);
      void room.leave();
    },
  };
}
